import { gradeFromScore } from "./score-calculator";

export const GRADE_COLORS: Record<string, string> = {
  A: "var(--color-grade-a)",
  B: "var(--color-grade-b)",
  C: "var(--color-grade-c)",
  D: "var(--color-grade-d)",
  F: "var(--color-grade-f)",
};

/** @deprecated Use `gradeFromScore` from `@/lib/score-calculator` directly. */
export const scoreToGrade = gradeFromScore;

export function getGradeColor(grade: string): string {
  const letter = grade.charAt(0).toUpperCase();
  return GRADE_COLORS[letter] || GRADE_COLORS.C;
}

export function countryToSlug(country: string): string {
  return country
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, "-");
}

/**
 * Localized / aliased country names that sometimes land in `formatted_address`
 * from Google Places. Maps back to ISO-3166 alpha-2 so any display variant
 * resolves deterministically. Keep lowercase.
 */
const COUNTRY_NAME_ALIASES: Record<string, string> = {
  // German
  "deutschland": "DE",
  "frankreich": "FR",
  "spanien": "ES",
  "italien": "IT",
  "österreich": "AT",
  "osterreich": "AT",
  "schweiz": "CH",
  "griechenland": "GR",
  "vereinigtes königreich": "GB",
  "vereinigtes konigreich": "GB",
  "vereinigte staaten": "US",
  // French
  "allemagne": "DE",
  "espagne": "ES",
  "italie": "IT",
  "autriche": "AT",
  "suisse": "CH",
  "grèce": "GR",
  "grece": "GR",
  "royaume-uni": "GB",
  "états-unis": "US",
  "etats-unis": "US",
  "pays-bas": "NL",
  "belgique": "BE",
  // Spanish / Portuguese
  "españa": "ES",
  "alemania": "DE",
  "francia": "FR",
  "italia": "IT",
  "países bajos": "NL",
  "paises bajos": "NL",
  "bélgica": "BE",
  "belgica": "BE",
  "grecia": "GR",
  "reino unido": "GB",
  "estados unidos": "US",
  "estados unidos de américa": "US",
  "estados unidos de america": "US",
  "alemanha": "DE",
  "frança": "FR",
  "franca": "FR",
  "itália": "IT",
  "bélgica ": "BE",
  "países baixos": "NL",
  "paises baixos": "NL",
  "reino unido da grã-bretanha e irlanda do norte": "GB",
  // Italian
  "germania": "DE",
  "spagna": "ES",
  "regno unito": "GB",
  "stati uniti": "US",
  "stati uniti d'america": "US",
  "paesi bassi": "NL",
  "grecia ": "GR",
  // Dutch
  "duitsland": "DE",
  "frankrijk": "FR",
  "spanje": "ES",
  "italië": "IT",
  "italie ": "IT",
  "oostenrijk": "AT",
  "zwitserland": "CH",
  "verenigd koninkrijk": "GB",
  "verenigde staten": "US",
  "griekenland": "GR",
  "nederland": "NL",
  "belgië": "BE",
  "belgie": "BE",
  // Polish
  "niemcy": "DE",
  "francja": "FR",
  "hiszpania": "ES",
  "włochy": "IT",
  "wlochy": "IT",
  "wielka brytania": "GB",
  "stany zjednoczone": "US",
  "holandia": "NL",
  "belgia": "BE",
  "grecja": "GR",
  "austria ": "AT",
  "szwajcaria": "CH",
  // Russian / Ukrainian (transliterated Latin occasionally appears)
  "германия": "DE",
  "франция": "FR",
  "испания": "ES",
  "италия": "IT",
  "великобритания": "GB",
  "соединённые штаты америки": "US",
  "соединенные штаты америки": "US",
  "сша": "US",
  "нідерланди": "NL",
  "нидерланды": "NL",
};

/** Convert a country name to its flag emoji via ISO 3166-1 alpha-2 regional indicators. */
export const COUNTRY_CODES: Record<string, string> = {
  afghanistan: "AF", albania: "AL", algeria: "DZ", andorra: "AD",
  angola: "AO", argentina: "AR", armenia: "AM", australia: "AU",
  austria: "AT", azerbaijan: "AZ", bahamas: "BS", bahrain: "BH",
  bangladesh: "BD", barbados: "BB", belarus: "BY", belgium: "BE",
  belize: "BZ", benin: "BJ", bhutan: "BT", bolivia: "BO",
  "bosnia and herzegovina": "BA", botswana: "BW", brazil: "BR",
  brunei: "BN", bulgaria: "BG", cambodia: "KH", cameroon: "CM",
  canada: "CA", chile: "CL", china: "CN", colombia: "CO",
  "costa rica": "CR", croatia: "HR", cuba: "CU", cyprus: "CY",
  "czech republic": "CZ", czechia: "CZ", denmark: "DK",
  "dominican republic": "DO", ecuador: "EC", egypt: "EG",
  "el salvador": "SV", estonia: "EE", ethiopia: "ET", fiji: "FJ",
  finland: "FI", france: "FR", georgia: "GE", germany: "DE",
  ghana: "GH", greece: "GR", guatemala: "GT", haiti: "HT",
  honduras: "HN", hungary: "HU", iceland: "IS", india: "IN",
  indonesia: "ID", iran: "IR", iraq: "IQ", ireland: "IE",
  israel: "IL", italy: "IT", jamaica: "JM", japan: "JP",
  jordan: "JO", kazakhstan: "KZ", kenya: "KE", kuwait: "KW",
  latvia: "LV", lebanon: "LB", libya: "LY", liechtenstein: "LI",
  lithuania: "LT", luxembourg: "LU", malaysia: "MY", maldives: "MV",
  malta: "MT", mexico: "MX", moldova: "MD", monaco: "MC",
  mongolia: "MN", montenegro: "ME", morocco: "MA", mozambique: "MZ",
  myanmar: "MM", namibia: "NA", nepal: "NP", netherlands: "NL",
  "new zealand": "NZ", nicaragua: "NI", nigeria: "NG",
  "north macedonia": "MK", norway: "NO", oman: "OM", pakistan: "PK",
  panama: "PA", paraguay: "PY", peru: "PE", philippines: "PH",
  poland: "PL", portugal: "PT", qatar: "QA", romania: "RO",
  russia: "RU", "saudi arabia": "SA", senegal: "SN", serbia: "RS",
  singapore: "SG", slovakia: "SK", slovenia: "SI", "south africa": "ZA",
  "south korea": "KR", spain: "ES", "sri lanka": "LK", sweden: "SE",
  switzerland: "CH", taiwan: "TW", tanzania: "TZ", thailand: "TH",
  tunisia: "TN", turkey: "TR", turkiye: "TR", ukraine: "UA",
  "united arab emirates": "AE", uae: "AE",
  "united kingdom": "GB", uk: "GB",
  "united states": "US", "united states of america": "US", usa: "US",
  uruguay: "UY", uzbekistan: "UZ", venezuela: "VE", vietnam: "VN",
};

/**
 * Canonical country display list. Name → the label to store in the DB
 * (matching what Google Places returns for `country` long name).
 * Derived from the COUNTRY_CODES map; a handful of names are normalized
 * to the Google-Places long form to keep DB values consistent.
 */
export interface CountryOption {
  name: string;
  code: string;
  flagUrl: string;
}

const DISPLAY_NAME_OVERRIDES: Record<string, string> = {
  czechia: "Czech Republic",
  turkiye: "Turkey",
  uae: "United Arab Emirates",
  uk: "United Kingdom",
  usa: "United States",
  "united states of america": "United States",
};

function titleCaseCountry(raw: string): string {
  return raw
    .split(" ")
    .map((w) =>
      w.length <= 3 && w !== "and"
        ? w.toUpperCase()
        : w.charAt(0).toUpperCase() + w.slice(1),
    )
    .join(" ");
}

const COUNTRY_OPTIONS_INTERNAL: CountryOption[] = (() => {
  const seen = new Map<string, CountryOption>();
  for (const [key, code] of Object.entries(COUNTRY_CODES)) {
    const display = DISPLAY_NAME_OVERRIDES[key] ?? titleCaseCountry(key);
    if (seen.has(display)) continue;
    seen.set(display, {
      name: display,
      code: code.toLowerCase(),
      flagUrl: `https://flagcdn.com/${code.toLowerCase()}.svg`,
    });
  }
  return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
})();

export const COUNTRY_OPTIONS: ReadonlyArray<CountryOption> =
  COUNTRY_OPTIONS_INTERNAL;

/**
 * Inverse of `COUNTRY_CODES`: ISO alpha-2 (uppercase) → canonical English
 * display name. Built once so anything with a country_code can render the
 * same label everywhere, regardless of what Google returned.
 */
const CODE_TO_DISPLAY: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  for (const [key, code] of Object.entries(COUNTRY_CODES)) {
    const upper = code.toUpperCase();
    if (out[upper]) continue;
    out[upper] = DISPLAY_NAME_OVERRIDES[key] ?? titleCaseCountry(key);
  }
  return out;
})();

/**
 * Resolve an arbitrary country input (long name in any supported locale,
 * alias, or alpha-2 code) to `{ code, name }` where `code` is the uppercase
 * ISO 3166-1 alpha-2 when known, and `name` is the canonical English display
 * label. Returns `null` when the input can't be mapped.
 */
export function normalizeCountry(
  input: string | null | undefined,
): { code: string; name: string } | null {
  if (!input) return null;
  const raw = input.trim();
  if (!raw) return null;

  // Alpha-2 code shortcut
  if (/^[A-Za-z]{2}$/.test(raw)) {
    const upper = raw.toUpperCase();
    const display = CODE_TO_DISPLAY[upper];
    if (display) return { code: upper, name: display };
  }

  const key = raw.toLowerCase();
  const code =
    COUNTRY_CODES[key] ??
    COUNTRY_NAME_ALIASES[key] ??
    null;
  if (!code) return null;
  return {
    code: code.toUpperCase(),
    name: CODE_TO_DISPLAY[code.toUpperCase()] ?? titleCaseCountry(key),
  };
}

export function countryToFlag(country: string): string {
  const norm = normalizeCountry(country);
  if (!norm) return "";
  return String.fromCodePoint(
    ...norm.code.split("").map((c) => 0x1f1e6 + c.charCodeAt(0) - 65),
  );
}

/** Return lowercase ISO 3166-1 alpha-2 code for a country name/code, or null. */
export function countryToCode(country: string): string | null {
  return normalizeCountry(country)?.code.toLowerCase() ?? null;
}

/**
 * Return the canonical English display name for a country name/code, or null
 * when the input can't be resolved. Callers that have a `country_code` column
 * should prefer this over trusting raw `country` strings.
 */
export function countryDisplayName(
  input: string | null | undefined,
): string | null {
  return normalizeCountry(input)?.name ?? null;
}

/** Return a flagcdn.com SVG URL for a country name/code, or null. */
export function countryFlagUrl(country: string): string | null {
  const code = countryToCode(country);
  return code ? `https://flagcdn.com/${code}.svg` : null;
}

const APP_URL = "https://app.rekkd.io";

/** JSON-LD ItemList for explore pages */
export function buildItemListJsonLd(
  items: { name: string; url: string; position: number }[],
  listName: string,
) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: listName,
    numberOfItems: items.length,
    itemListElement: items.map((item) => ({
      "@type": "ListItem",
      position: item.position,
      name: item.name,
      url: `${APP_URL}${item.url}`,
    })),
  };
}
